import React, { useState, useEffect, useRef } from 'react';
import { formatNumber } from '../../utils/NumberUtils';

/**
 * props:
 * - data          -> report de fluxo de caixa (série vendas−compras), respeita o filtro do topo
 * - realized      -> { series:[{date,realized,pct,trades}], totalRealized, totalPct, totalTrades } (spot, 30d)
 * - showRealized  -> true em spot (mostra a aba de rentabilidade). Em futuros: só fluxo de caixa.
 *
 * Duas abas no mesmo card:
 *   'pct'      (padrão) -> linha do % de retorno por dia (mesma fonte da tabela de baixo)
 *   'cashflow'          -> gráfico atual de fluxo de caixa
 */
function LineChart(props) {

    const chartRef = useRef(null);

    const [report, setReport] = useState({
        profit: 0,
        profitPerc: 0,
        sellVolume: 0,
        buyVolume: 0,
        series: [],
        subs: []
    });

    const [realized, setRealized] = useState({ series: [], totalRealized: 0, totalPct: 0, totalTrades: 0 });
    const [activeTab, setActiveTab] = useState('pct');

    useEffect(() => {
        if (props.data) setReport(props.data);
    }, [props.data]);

    useEffect(() => {
        if (props.realized) setRealized(props.realized);
    }, [props.realized]);

    const showRealized = props.showRealized !== false;
    const effectiveTab = showRealized ? activeTab : 'cashflow';

    // Em futuros não há aba de rentabilidade — garante o estado coerente.
    useEffect(() => {
        if (!showRealized && activeTab !== 'cashflow') setActiveTab('cashflow');
    }, [showRealized, activeTab]);

    const pctSeries = (realized && realized.series) ? realized.series : [];

    useEffect(() => {
        if (chartRef.current) {
            try { chartRef.current.detach(); } catch (e) { /* noop */ }
            chartRef.current = null;
        }

        if (effectiveTab === 'pct') {
            if (!pctSeries.length) return; // sem trades fechados: mostra a mensagem no lugar do gráfico

            // não poluir o eixo X quando tiver muitos dias
            const mod = pctSeries.length > 20 ? 2 : 1;
            const labels = pctSeries.map((d, i) => (i % mod === 0 ? `${d.date.slice(8)}/${d.date.slice(5, 7)}` : ''));
            const points = pctSeries.map(d => ({
                value: d.pct,
                meta: `${d.date.slice(8)}/${d.date.slice(5, 7)}: ${getText(d.pct)}% · ${getText(d.realized)} ${realized.quote || report.quote || ''}`
            }));

            chartRef.current = new window.Chartist.Line('.ct-beholder-chart-line', {
                labels,
                series: [points]
            }, {
                showArea: true,
                fullWidth: true,
                chartPadding: { right: 20 },
                axisX: { showGrid: true },
                axisY: {
                    showGrid: true,
                    showLabel: true,
                    labelInterpolationFnc: v => `${formatNumber(v, 2)}%`
                },
                plugins: [window.Chartist.plugins.tooltip({ appendToBody: true, tooltipFnc: (meta) => meta })]
            });
            return;
        }

        // aba de fluxo de caixa (comportamento original)
        if (!report || !report.series) return;
        const mod = report.subs.length > 20 ? 2 : 1;
        let cont = 0;
        const subs = report.subs.map(s => (cont++ % mod === 0 ? s : ''));

        chartRef.current = new window.Chartist.Line('.ct-beholder-chart-line', {
            labels: subs,
            series: [report.series]
        }, {
            showArea: true,
            fullWidth: true,
            chartPadding: { right: 20 },
            axisX: { showGrid: true },
            axisY: { showGrid: true, showLabel: true }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveTab, report, realized]);

    function formattedValue(value) {
        return formatNumber(value, 2);
    }

    function getText(value) {
        const signal = value > 0 ? '+' : '';
        return signal + (value ? formattedValue(value) : value);
    }

    function getTextClass(value) {
        return parseFloat(value) > 0 ? 'text-success' : 'text-danger';
    }

    const isPct = effectiveTab === 'pct';

    return (
        <React.Fragment>
            <div className='row'>
                <div className='col-12 mb-4'>
                    <div className='card bg-yellow-100 border-0 shadow'>
                        {showRealized && (
                            <ul className='nav nav-tabs px-3 pt-2' role='tablist'>
                                <li className='nav-item' role='presentation'>
                                    <button
                                        type='button'
                                        className={`nav-link${isPct ? ' active' : ''}`}
                                        onClick={() => setActiveTab('pct')}
                                        role='tab'>
                                        Rentabilidade diária
                                    </button>
                                </li>
                                <li className='nav-item' role='presentation'>
                                    <button
                                        type='button'
                                        className={`nav-link${!isPct ? ' active' : ''}`}
                                        onClick={() => setActiveTab('cashflow')}
                                        role='tab'>
                                        Fluxo de caixa
                                    </button>
                                </li>
                            </ul>
                        )}
                        <div className='card-header d-sm-flex flex-row align-item-center flex-0'>
                            {isPct ? (
                                <React.Fragment>
                                    <div className='d-block mb-3 mb-sm-0'>
                                        <h2 className='fs-3 fw-extrabold mb-0'>{report.quote || ''} {realized.totalRealized != null ? getText(realized.totalRealized) : ''}</h2>
                                        <small className='text-muted'>Rentabilidade diária — % de retorno por trade fechado a cada dia (últimos 30 dias, spot). É o lucro realizado da tabela abaixo, no gráfico.</small>
                                    </div>
                                    {realized.totalPct != null && realized.totalPct !== 0 && (
                                        <div className='d-block ms-3'>
                                            <div className='small'>
                                                <span className={getTextClass(realized.totalPct) + ' fw-bold'}>
                                                    ({getText(realized.totalPct)}%)
                                                </span>
                                                {realized.totalTrades != null && <span className='text-muted ms-1'>· {realized.totalTrades} trades</span>}
                                            </div>
                                        </div>
                                    )}
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <div className='d-block mb-3 mb-sm-0'>
                                        <h2 className='fs-3 fw-extrabold mb-0'>{report.quote || ''} {report.sellVolume != null && report.buyVolume != null ? getText(report.sellVolume - report.buyVolume) : ''}</h2>
                                        <small className='text-muted'>Fluxo de caixa (vendas − compras) — negativo = capital ainda investido em posições abertas, não prejuízo. O lucro fechado está na tabela abaixo.</small>
                                    </div>
                                    {report.profitPerc ? (
                                        <div className='d-block ms-3'>
                                            <div className='small'>
                                                <span className={getTextClass(report.profitPerc) + ' fw-bold'}>
                                                    ({getText(report.profitPerc)}%)
                                                </span>
                                            </div>
                                        </div>
                                    ) : <React.Fragment></React.Fragment>}
                                </React.Fragment>
                            )}
                        </div>
                        <div className='card-body p-2'>
                            {isPct && !pctSeries.length ? (
                                <div className='text-muted small p-3'>Sem trades fechados (compra→venda) nos últimos 30 dias — a rentabilidade só aparece quando uma posição é vendida.</div>
                            ) : (
                                <div className='ct-beholder-chart-line ct-double-octave'></div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
}

export default LineChart;
